<template>
  <div class="ModularRequestTenderPage">
    <JoszakiLoading v-if="showLoader" active />
    <component
      :is="resolvedComponent"
      v-else-if="resolvedComponent"
      :key="resolvedKey"
      v-bind="resolvedProps"
      :current-step="currentStep"
      :total-steps="totalSteps"
      v-on="resolvedListeners"
    />
    <div v-else class="text-center py-8 text-gray-500">
      {{ $t("requestTender.stepNotFound", { step: currentStep }) }}
    </div>
  </div>
</template>

<script>
import {
  computed,
  ref,
  toRefs,
  watch,
  onMounted,
  useFetch,
  useRoute,
} from "@nuxtjs/composition-api";
import { autoSkipAlreadyDone, markAutoSkipDone } from "./autoSkipGuard";
import {
  FIXED_STEP_COMPONENTS,
  REQUEST_TENDER_PAGE_MODULES,
  getBaseSlug,
} from "~/config/requestTenderPageModules";
import useRequestTenderPageModules from "~/composables/useRequestTenderPageModules";
import useRequestTenderPageFunnel from "~/composables/useRequestTenderPageFunnel";
import { useTrackWizardStepViewed } from "~/composables/tracking/useTrackWizardClick";
import { useNewTender } from "~/composables/tender/useNewTender";

const SLUG_TO_FIELD = {
  name: "name",
  "job-description": "jobDescription",
  city: "city",
  email: "email",
  phone: "phone",
  profession: "profession",
  terms: "terms",
};

const CUSTOM_QUESTION_LOADER = () => import("./CustomQuestionStepModule.vue");

const isCustomQuestion = (mod) => mod?.content?.type === "custom-question";

export default {
  name: "ModularRequestTenderPage",
  props: {
    activeTab: { type: Number, required: true },
    isLoading: { type: Boolean, default: false },
  },
  emits: ["goto-step", "submit"],
  setup(props, { emit }) {
    const { activeTab } = toRefs(props);
    const newTender = useNewTender();
    const route = useRoute();
    const {
      visibleModules,
      loaded,
      loading,
      resolve,
      versionId,
      resolvedForProfessionId,
      resolvedOk,
    } = useRequestTenderPageModules();
    const funnel = useRequestTenderPageFunnel();
    const { trackWizardStepViewed } = useTrackWizardStepViewed();
    let lastViewedStepKey = null;

    const getComponent = (mod) => {
      if (isCustomQuestion(mod)) return CUSTOM_QUESTION_LOADER;
      const slug = mod.slug;
      const loader =
        REQUEST_TENDER_PAGE_MODULES[slug] ||
        REQUEST_TENDER_PAGE_MODULES[getBaseSlug(slug)];
      return loader || null;
    };

    const componentCache = {};
    const cache = (key, loader) => {
      if (!componentCache[key]) {
        componentCache[key] = () => loader();
      }
      return componentCache[key];
    };

    /**
     * Full ordered sequence: [profession, ...visibleMiddleModules, terms].
     * Each entry knows its 1-based position and the fieldName used by
     * useNewTender / validation messages.
     */
    const sequence = computed(() => {
      const middle = visibleModules.value.map((m, i) => ({
        slug: m.slug,
        index: i + 2,
        fieldName: SLUG_TO_FIELD[getBaseSlug(m.slug)] || m.slug,
        loader: getComponent(m),
        moduleConfig: m,
        isCustom: isCustomQuestion(m),
      }));
      return [
        {
          slug: "profession",
          index: 1,
          fieldName: "profession",
          loader: FIXED_STEP_COMPONENTS.profession,
          moduleConfig: null,
          isCustom: false,
        },
        ...middle,
        {
          slug: "terms",
          index: middle.length + 2,
          fieldName: "terms",
          loader: FIXED_STEP_COMPONENTS.terms,
          moduleConfig: null,
          isCustom: false,
        },
      ];
    });

    const totalSteps = computed(() => sequence.value.length);

    const stepDescriptors = computed(() =>
      sequence.value.map((s) => ({
        index: s.index,
        fieldName: s.fieldName,
        slug: s.slug,
        isCustom: !!s.isCustom,
        label: s.moduleConfig?.label || null,
      }))
    );

    // Walk forward through the sequence starting at `fromIndex`, skipping
    // any step whose data is already supplied by the URL. Trust the URL
    // while prefill is still in flight (so a slow network doesn't make the
    // user re-enter info they already supplied), but if prefill has
    // FINISHED and the corresponding value is still empty, the URL id was
    // invalid — fall through to the picker step instead of skipping,
    // otherwise the wizard would submit with `professionId: NaN` /
    // `cityId: NaN`.
    const findNextNonPresetStep = (fromIndex) => {
      const seq = sequence.value;
      let target = fromIndex;
      const prefillDone = !!newTender.state.prefilled;
      while (target <= seq.length) {
        const step = seq[target - 1];
        if (!step) break;
        const query = route.value?.query || {};
        const professionLoaded = !!newTender.profession.value?.id;
        const cityLoaded = !!newTender.city.value?.id;
        if (
          step.slug === "profession" &&
          query.professionId &&
          (!prefillDone || professionLoaded)
        ) {
          target++;
          continue;
        }
        if (
          step.slug === "city" &&
          query.cityId &&
          (!prefillDone || cityLoaded)
        ) {
          target++;
          continue;
        }
        break;
      }
      return target;
    };

    // Whether the current step should be auto-skipped because its value is
    // already supplied by the URL (e.g. landing on the profession step with
    // ?professionId set). Returns the step to jump to, or null when no skip
    // is needed. Computed so it works identically on the server and client.
    const pendingSkipTarget = computed(() => {
      if (!loaded.value || !sequence.value.length) return null;
      const target = findNextNonPresetStep(activeTab.value);
      return target !== activeTab.value && target <= sequence.value.length
        ? target
        : null;
    });

    // The auto-skip is a one-shot — it should fire on the user's first
    // arrival at a preset step, not whenever activeTab transiently equals a
    // preset step again. Without this flag, browser-back from a later step
    // to the prefilled profession/city step would re-trigger the skip and
    // bounce the user forward, making it impossible to edit those prefilled
    // values; the same flag also collapses any race between the onMounted
    // emit and the watch emit into a single navigation.
    const autoSkipDecided = ref(false);

    // Positions [entry tab, this value) are the steps the INITIAL auto-skip
    // jumps over — phantoms the user never sees, so they must NOT emit a
    // `step_view`. Cleared by the activeTab watcher once the wizard has
    // navigated past them; after that, landing on a preset step again is a
    // deliberate back-navigation that the user actually sees, so it counts as
    // a real view (and the read side reclassifies that session away from
    // auto-skipped).
    const pendingSkipPhantomUntil = ref(null);

    // Show the loader only while the config is still loading, or while the
    // INITIAL auto-skip is pending (so we never flash the to-be-skipped
    // step on first arrival). Once the decision has been made — or the
    // user has navigated back to a preset step — we render the real step
    // so they can edit it. Holds during SSR too, so the actual form ends
    // up in the server HTML instead of a lone spinner.
    const showLoader = computed(() => {
      if (!loaded.value) return true;
      if (!autoSkipDecided.value && pendingSkipTarget.value != null) {
        return true;
      }
      return false;
    });

    // Watch versionId and push it into useNewTender so it's included in
    // the submit payload alongside the captured custom-question answers.
    // When the active version genuinely changes from one known id to a
    // different one (e.g. admin published a new config while the user was
    // mid-wizard, or a profession-specific override is loaded after the
    // global config), drop the in-memory answers — they were captured
    // against the previous schema and may have stale slug/type pairs.
    watch(
      versionId,
      (newId) => {
        const previous = newTender.formData.moduleVersionId;
        if (previous && newId && previous !== newId) {
          newTender.clearAllModuleAnswers();
        }
        newTender.setModuleVersionId(newId);
      },
      { immediate: true }
    );

    // Emit a `step_skip_auto_filled` event for every step in the half-open
    // range [from, to) the wizard auto-skips because the URL already supplied
    // its value. The conductor funnel counts these toward the step (keeping it
    // monotonic — without them a session entered with ?professionId would
    // never register for step 1, so step 1 would show fewer sessions than
    // step 2) while keeping them distinguishable from steps the user actually
    // viewed.
    const trackSkippedSteps = (from, to) => {
      for (let i = from; i < to; i++) {
        const step = sequence.value[i - 1];
        if (!step) continue;
        funnel.trackStepSkipAutoFilled({
          moduleVersionId: versionId.value || null,
          professionId: newTender.profession.value?.id || null,
          stepSlug: step.slug,
          stepPosition: step.index,
          totalSteps: totalSteps.value,
        });
      }
    };

    // Await any in-flight resolve, then re-attempt a transiently-failed one
    // (resolvedOk === false) so a backend hiccup doesn't leave the user
    // navigating against the default module set instead of the profession
    // override. Bounded to the user-driven `next` checkpoint — at most one
    // retry per advance, so a persistent outage can't spin.
    const ensureResolved = async () => {
      // If a profession-specific resolve() is still in flight (e.g. the user
      // hammered Enter on the profession autocomplete and the override
      // config hasn't loaded yet), wait for it. Otherwise we'd compute the
      // next step against the stale global sequence — possibly skipping or
      // duplicating a step once the override reshuffles things a moment
      // later.
      if (loading.value) {
        await new Promise((_resolve) => {
          const stop = watch(loading, (v) => {
            if (!v) {
              stop();
              _resolve();
            }
          });
        });
      }
      const professionId = currentProfessionId();
      if (needsResolve(professionId)) {
        await resolve({ professionId });
      }
    };

    const onNext = async () => {
      await ensureResolved();
      // After every `next` event, skip past any middle steps whose values
      // are already supplied by the URL (e.g. city when ?cityId= is set).
      const target = findNextNonPresetStep(activeTab.value + 1);
      if (target > totalSteps.value) return;
      // The user never sees the skipped middle steps, but the funnel still
      // counts them — flagged auto-skipped — so it stays monotonic.
      trackSkippedSteps(activeTab.value + 1, target);
      emit("goto-step", target);
    };

    const onProfessionSelected = async (profession) => {
      if (profession?.id) {
        await resolve({ professionId: profession.id });
      }
    };

    // A hidden step never renders, but its underlying field can still be
    // pre-filled (localStorage from a previous tender, the `?description=`
    // query param, the createJob cookie). Wipe the data backing any hidden
    // step right before submit so it doesn't silently carry over a stale value
    // (a hidden "Name" / custom question must not submit a restored answer).
    // EXCEPTION: a description passed via `?description=` was supplied for THIS
    // tender on purpose (e.g. from a QuickOffer-style box), so it is kept even
    // when the description step is hidden.
    const clearHiddenStepData = () => {
      const visibleSlugs = new Set(
        visibleModules.value.map((m) => getBaseSlug(m.slug))
      );
      if (!visibleSlugs.has("name")) newTender.name.value = "";
      if (!visibleSlugs.has("job-description")) {
        const queryDescription = route.value?.query?.description;
        newTender.jobDescription.value = Array.isArray(queryDescription)
          ? queryDescription[0] || ""
          : queryDescription || "";
      }
      Object.keys(newTender.moduleAnswers.value || {}).forEach((slug) => {
        if (!visibleSlugs.has(getBaseSlug(slug))) {
          newTender.clearModuleAnswer(slug);
        }
      });
    };

    const onSubmit = () => {
      clearHiddenStepData();
      emit("submit");
    };

    const currentStepConfig = computed(() => {
      const step = sequence.value[activeTab.value - 1];
      if (!step || !step.loader) return null;
      const stepProps = {};
      const listeners = { next: onNext };

      if (step.slug === "profession") {
        listeners["profession-selected"] = onProfessionSelected;
      } else if (step.slug === "terms") {
        stepProps.loading = props.isLoading;
        stepProps.stepDescriptors = stepDescriptors.value;
        listeners.submit = onSubmit;
        listeners["goto-step"] = (i) => emit("goto-step", i);
      } else if (step.isCustom) {
        stepProps.moduleConfig = step.moduleConfig;
      }
      if (!step.isCustom) {
        stepProps.moduleSlug = step.slug;
      }

      // Custom modules share a single component but vary by slug, so include
      // the slug in the cache key to force re-instantiation when the
      // user navigates to a different custom step.
      const cacheKey = step.isCustom
        ? `custom-${step.slug}`
        : getBaseSlug(step.slug);

      return {
        slug: step.slug,
        component: cache(cacheKey, step.loader),
        props: stepProps,
        listeners,
      };
    });

    // The profession the config should be resolved for. Prefer the URL
    // `professionId` over `newTender.profession.value?.id` because
    // `prefillWithIds` runs in parallel and might not have populated the state
    // yet — in that case we'd fetch the global config and miss the
    // profession-specific override.
    const currentProfessionId = () =>
      route.value?.query?.professionId ||
      newTender.profession.value?.id ||
      null;

    // Re-resolve unless we already hold a SUCCESSFULLY resolved config for this
    // exact profession. `resolvedOk` distinguishes a definitive answer (a real
    // config, or a genuine "no config" null) from a transient fetch failure —
    // the latter must stay retryable instead of being cached as success.
    // Comparing the profession (not a bare `loaded`) also covers SPA re-entry
    // into the wizard after the singleton already holds a config for a
    // different (or no) profession.
    const needsResolve = (professionId) => {
      if (loading.value) return false;
      const current = professionId != null ? String(professionId) : null;
      return !(
        loaded.value &&
        resolvedOk.value &&
        resolvedForProfessionId.value === current
      );
    };

    // Resolve the module config on the server AND the client. Using useFetch
    // (instead of an onMounted-only call) bakes the resolved modules into the
    // SSR HTML, so the first paint is the real form rather than a spinner that
    // only fills in after the JS bundle downloads and runs a second round-trip.
    useFetch(async () => {
      const professionId = currentProfessionId();
      if (needsResolve(professionId)) await resolve({ professionId });
    });

    // `useFetch` does not re-run on the client after it ran during SSR, so a
    // transient SSR-side resolve failure (`resolvedOk === false`) would
    // otherwise leave the user on the default config for the whole page load.
    // Retry once on mount to self-heal it.
    onMounted(() => {
      const professionId = currentProfessionId();
      if (needsResolve(professionId)) resolve({ professionId });
    });

    // The actual auto-skip navigation can only happen on the client (the
    // server can't emit a step change). Make the decision exactly once per
    // wizard ENTRY (see ./autoSkipGuard): on mount if the config is already
    // loaded (SSR happy path) or as soon as it loads if it raced.
    const tryInitialAutoSkip = () => {
      if (autoSkipDecided.value) return;
      if (!loaded.value || !sequence.value.length) return;
      if (autoSkipAlreadyDone()) {
        // Already auto-skipped earlier in this wizard entry — honor whatever
        // step the user is now on instead of bouncing them again.
        autoSkipDecided.value = true;
        return;
      }
      const target = pendingSkipTarget.value;
      autoSkipDecided.value = true;
      markAutoSkipDone();
      if (target != null) {
        // Mark [activeTab, target) as phantoms so the visibleStep watcher
        // doesn't emit a view for the step we're skipping past on entry —
        // cleared once we've navigated to `target`.
        pendingSkipPhantomUntil.value = target;
        // Count the preset steps the user jumps over on entry (e.g. profession
        // when ?professionId is set) as auto-skipped — see onNext.
        trackSkippedSteps(activeTab.value, target);
        emit("goto-step", target);
      }
    };
    onMounted(tryInitialAutoSkip);
    watch(loaded, () => tryInitialAutoSkip());

    // Once the entry auto-skip has actually moved past the preset step(s),
    // stop treating those positions as phantoms — a later visit is a
    // deliberate back-navigation and should be tracked as a real view.
    watch(activeTab, (tab) => {
      if (
        pendingSkipPhantomUntil.value != null &&
        tab >= pendingSkipPhantomUntil.value
      ) {
        pendingSkipPhantomUntil.value = null;
      }
    });

    // The route middleware can only sanity-bound the step number (the real
    // sequence length isn't known until the conductor config resolves). A
    // step beyond the resolved sequence — stale bookmark, config shrank,
    // hand-edited URL — would render the dead-end "Step not found"
    // placeholder; restart the wizard instead. Client-only, like the
    // auto-skip: the server can't emit a step change.
    watch(
      [loaded, activeTab, totalSteps],
      ([isLoaded, tab, total]) => {
        if (process.client && isLoaded && total > 0 && tab > total) {
          emit("goto-step", 1);
        }
      },
      { immediate: true }
    );

    // Funnel telemetry — emit a `step_view` event every time the resolved
    // step the user actually SEES changes. We key the watcher on the
    // sequence index + slug rather than on `activeTab` alone so that
    // auto-skipped steps (e.g. profession when ?professionId is set) don't
    // get a phantom view. `loaded`-gated so we have a real moduleVersionId
    // to attribute the event to; `autoSkipDecided`-gated so we don't fire
    // for the initial to-be-skipped step that the user never actually saw.
    const visibleStep = computed(() => {
      if (!loaded.value) return null;
      if (!autoSkipDecided.value) return null;
      // Suppress only the initial to-be-skipped step(s) — the phantoms the
      // user never sees on entry. A preset step the user navigates BACK to
      // (after the one-shot auto-skip already fired) renders for real and so
      // emits a genuine `step_view`; the read side then reclassifies that
      // session away from auto-skipped for the step.
      if (
        pendingSkipPhantomUntil.value != null &&
        activeTab.value < pendingSkipPhantomUntil.value
      ) {
        return null;
      }
      const step = sequence.value[activeTab.value - 1];
      if (!step) return null;
      return { slug: step.slug, position: step.index };
    });
    watch(
      visibleStep,
      (step) => {
        if (!step) return;
        funnel.trackStepView({
          moduleVersionId: versionId.value || null,
          professionId: newTender.profession.value?.id || null,
          stepSlug: step.slug,
          stepPosition: step.position,
          totalSteps: totalSteps.value,
        });
        // `visibleStep` yields a fresh object whenever the sequence is
        // re-resolved (e.g. after the profession is picked), which would
        // report the same step as viewed twice.
        const viewedStepKey = `${step.slug}:${step.position}`;
        if (viewedStepKey === lastViewedStepKey) return;
        lastViewedStepKey = viewedStepKey;
        trackWizardStepViewed({
          step: step.position,
          moduleSlug: step.slug,
          totalSteps: totalSteps.value,
        });
      },
      { immediate: true }
    );

    // Re-resolve whenever the selected profession changes — including when it
    // is populated asynchronously by prefill (null -> id), switched to another
    // profession (idA -> idB), or cleared (id -> null, back to the global
    // config). The `needsResolve` guard keeps this from duplicating the
    // useFetch / onProfessionSelected resolves.
    watch(
      () => newTender.profession.value?.id,
      async (id) => {
        if (needsResolve(id ?? null)) {
          await resolve({ professionId: id ?? null });
        }
      }
    );

    return {
      showLoader,
      currentStep: computed(() => activeTab.value),
      totalSteps,
      currentStepConfig,
      // Flat fields so the template never reads `.component` from null.
      resolvedComponent: computed(
        () => currentStepConfig.value?.component || null
      ),
      resolvedKey: computed(() => {
        const c = currentStepConfig.value;
        return c ? `${c.slug}-${activeTab.value}` : "";
      }),
      resolvedProps: computed(() => currentStepConfig.value?.props || {}),
      resolvedListeners: computed(
        () => currentStepConfig.value?.listeners || {}
      ),
    };
  },
};
</script>
